import React, { useState, useEffect } from "react";
import "./SearchBar.css";
import {
    FormControl,
    Box,
    MenuItem, Select,
    Autocomplete,
    TextField,
    IconButton,
    Tooltip
} from "@mui/material";
import MyLocationIcon from "@mui/icons-material/MyLocation";

import { useNavigate, useLocation } from "react-router-dom";
import queryString from "query-string";
import UseWindowDimensions from "./GetWindowDimensions";
import { Constants } from "../Constants";
import { ProvidersService } from "../services/ProvidersService";
import { GeoLocationService } from "../services/GeoLocationService";

export const DefaultDistance = 25;
export const DefaultAvailability = "all";

function SearchBar() {
    const [distance, setDistance] = useState(25);
    const [availability, setAvailability] = useState("all");
    const navigate = useNavigate();
    const parsed = queryString.parse(window.location.search);
    const hasToken = window.location.hash != false;
    const { width } = UseWindowDimensions();
    const [isMobile, setIsMobile] = useState(width <= Constants.MOBILE_WIDTH)
    const [margin, setMargin] = useState(20);
    const [Page, setPage] = useState(parsed.page ? parseInt(parsed.page) : 1);

    const location = useLocation();

    const hasBounds = parsed.minLatitude && parsed.maxLatitude && parsed.minLongitude && parsed.maxLongitude;

    const [cityStateOptions, setCityStateOptions] = useState([]);
    const initialCityState = (parsed.city && parsed.state) ? `${parsed.city}, ${parsed.state}` : null;
    const [selectedCityState, setSelectedCityState] = useState(initialCityState);

    useEffect(() => {
        const urlPage = parsed.page ? parseInt(parsed.page) : 1;
        if (urlPage !== Page) {
            setPage(urlPage);
        }
    }, [location.search]);

    useEffect(() => {
        const urlDistance = Number.parseFloat(parsed.distance);
        if (parsed.distance != null && !Number.isNaN(urlDistance) && urlDistance !== Number(distance)) {
            setDistance(urlDistance);
        }
    }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        let cancelled = false;
        ProvidersService.getCityStateList()
            .then(list => {
                if (cancelled) return;
                setCityStateOptions(list.map(cs => `${cs.city}, ${cs.state}`));
            })
            .catch(err => {
                if (cancelled) return;
                console.error("Failed to load city/state list:", err);
            });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        var d = DefaultDistance;
        var a = DefaultAvailability;

        try {
            d = parsed.distance;
            if (!d)
                d = DefaultDistance;
        }
        catch (e) {
            console.log(e)
        }
        setDistance(d);

        try {
            a = parsed.availability
            if (!a)
                a = DefaultAvailability;
        }
        catch (e) {
            console.log(e);
        }
        setAvailability(a);
    }, [])

    useEffect(() => {
        var w = (0.5 * (width - 124 - 55) - 150 - 140);
        if (isMobile && width - (w + 280 + 40 + 60) < 0) w = 0;
        if (w < 0) w = 0;
        setMargin(w);
    }, [width, isMobile])

    useEffect(() => { setIsMobile(width <= Constants.MOBILE_WIDTH) }, [width]);

    useEffect(() => {
        if (!hasToken) navigate('?latitude=' + parsed.latitude + '&longitude=' + parsed.longitude + '&distance=' + parsed.distance + '&accuracy=' + parsed.accuracy + '&category=' + parsed.category + '&availability=' + availability + '&page=' + Page, { replace: true });
    }, [availability])

    const handleSelectDistance = (e) => {
        const newDistance = Number(e.target.value);
        setDistance(newDistance);
        const cityParams = (parsed.city && parsed.state) ? `&city=${encodeURIComponent(parsed.city)}&state=${encodeURIComponent(parsed.state)}` : '';

        if (hasBounds) {
            // Exiting area mode: convert to radius search centered on rect center
            const centerLat = (Number(parsed.minLatitude) + Number(parsed.maxLatitude)) / 2;
            const centerLon = (Number(parsed.minLongitude) + Number(parsed.maxLongitude)) / 2;
            if (!hasToken) navigate('?latitude=' + centerLat + '&longitude=' + centerLon + '&distance=' + newDistance + '&accuracy=0.01&category=' + (parsed.category || "") + '&availability=' + (parsed.availability || "") + '&page=1', { replace: true });
        } else {
            if (!hasToken) navigate('?latitude=' + parsed.latitude + '&longitude=' + parsed.longitude + '&distance=' + newDistance + '&accuracy=' + parsed.accuracy + cityParams + '&category=' + parsed.category + '&availability=' + parsed.availability + '&page=' + Page, { replace: true });
        }
    }

    const handleAvailabilitySelect = (e) => {
        setAvailability(e.target.value);
    }

    const standardDistances = [1, 5, 25, 50, 100, 12500];
    const numericDistance = Number(distance);
    const selectValue = hasBounds ? "" : (Number.isNaN(numericDistance) ? DefaultDistance : numericDistance);

    const returnToMyLocation = async () => {
        try {
            const loc = await GeoLocationService.getGeoLocation();
            const accuracyMiles = loc.accuracy * 0.62137119;
            navigate(`?latitude=${loc.latitude}&longitude=${loc.longitude}&distance=${DefaultDistance}&accuracy=${accuracyMiles}&category=${parsed.category || ""}&availability=${parsed.availability || ""}&page=1`, { replace: true });
        } catch (error) {
            console.error("Geolocation lookup failed:", error);
            // Fall back to a bare URL; the home page re-acquires location when geo params are absent.
            navigate(`?category=${parsed.category || ""}&availability=${parsed.availability || ""}&page=1`, { replace: true });
        }
    };

    const handleCityStateChange = async (event, newValue) => {
        setSelectedCityState(newValue);
        if (newValue) {
            const idx = newValue.lastIndexOf(",");
            const city = newValue.slice(0, idx).trim();
            const state = newValue.slice(idx + 1).trim();
            try {
                const cityCenter = await ProvidersService.getCityCenter(city, state);
                // Navigate to geo mode with city center as the search center
                navigate(`?latitude=${cityCenter.latitude}&longitude=${cityCenter.longitude}&distance=${cityCenter.radiusMiles}&accuracy=0.01&city=${encodeURIComponent(city)}&state=${encodeURIComponent(state)}&category=${parsed.category || ""}&availability=${parsed.availability || ""}&page=1`, { replace: true });
            } catch (error) {
                // Fall back to city-string mode on error (404/network)
                console.error("Failed to fetch city center, falling back to city-string mode:", error);
                navigate(`?city=${encodeURIComponent(city)}&state=${encodeURIComponent(state)}&category=${parsed.category || ""}&availability=${parsed.availability || ""}&page=1`, { replace: true });
            }
        } else {
            // Selection cleared: return to normal location-aware operation by
            // re-acquiring the user's current location (the URL's geo params
            // currently hold the previously selected city's center).
            await returnToMyLocation();
        }
    };

    return (
        <Box display={"flex"} margin={"auto"} justifyContent={"center"}>
            <Box display={"flex"} border={0} borderColor={"gray"}  borderRadius={5} paddingX={isMobile ? "8px" : "10px"} boxShadow={"5"} width={"100%"}>
                <FormControl sx={{ flexShrink: 0 }}>
                    {/*<InputLabel style={{textAlign:"center", width:"100%", top:"10px"}}>Search Distance</InputLabel>*/}
                    <Select value={selectValue} onChange={handleSelectDistance}
                        displayEmpty={hasBounds}
                        renderValue={hasBounds ? () => "Map area" : undefined}
                        sx={{ textAlign: "center",
                        height: "38px",
                        width: "max-content",
                        marginRight: "20px",
                        '.MuiOutlinedInput-notchedOutline': { border: 0 },
                        '& .MuiSelect-select': { whiteSpace: 'nowrap' }
                        }}>
                        {!hasBounds && !standardDistances.includes(selectValue) && <MenuItem value={selectValue}>{selectValue} Miles</MenuItem>}
                        <MenuItem value={1}>1 Mile</MenuItem>
                        <MenuItem value={5}>5 Miles</MenuItem>
                        <MenuItem value={25}>25 Miles</MenuItem>
                        <MenuItem value={50}>50 Miles</MenuItem>
                        <MenuItem value={100}>100 Miles</MenuItem>
                        <MenuItem value={12500}>Anywhere</MenuItem>
                    </Select>
                </FormControl>
                <Autocomplete
                    size="small"
                    options={cityStateOptions}
                    value={selectedCityState}
                    onChange={handleCityStateChange}
                    sx={{ flex: 1, minWidth: 0 }}
                    renderInput={(params) => <TextField {...params} placeholder={isMobile ? "City" : "City, State"} variant="outlined" sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }} />}
                    // Width math on mobile (~360px viewport):
                    //   logo (~50px abs left) + toolbar avatar overlay (~60px abs right)
                    //   = ~110px reserved → ~220px usable for the search pill.
                    // The distance FormControl is flexShrink 0, so the select keeps its
                    // max-content width (~95px) plus its 20px margin and its arrow is never
                    // drawn over the input. The Autocomplete takes the remaining width
                    // (flex 1, minWidth 0) instead of asking for 100%, so the row cannot
                    // overflow. That leaves the City input roughly 90px at 360px (estimate,
                    // not measured) for the placeholder, popup and clear icons.
                    // Desktop keeps roomy 130/220.

                    slotProps={{paper: {
                        sx:{
                            width: isMobile ? 200 : 220
                        }
                    }}}
                    data-testid="city-state-autocomplete"
                />
                {hasBounds && (
                    <Tooltip title="Return to my location" arrow>
                        <IconButton
                            onClick={returnToMyLocation}
                            size="small"
                            color="primary"
                            sx={{ alignSelf: "center", flexShrink: 0 }}
                            data-testid="return-to-my-location"
                        >
                            <MyLocationIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {/*<FormControl fullWidth style={{"display":"inline"}}>*/}
                {/*    /!*<InputLabel style={{textAlign:"center", width:"100%", top:"10px"}}>Availability</InputLabel>*!/*/}
                {/*    <Select value={availability} onChange={handleAvailabilitySelect}*/}
                {/*        sx={{minWidth:"130px",textAlign:"center", height: "38px", '.MuiOutlinedInput-notchedOutline': { border: 0 }}}>*/}
                {/*        <MenuItem value={"today"}>Today</MenuItem>*/}
                {/*        <MenuItem value={"nextweek"}>Next Week</MenuItem>*/}
                {/*        <MenuItem value={"all"}>Any Time</MenuItem>*/}
                {/*    </Select>*/}
                {/*</FormControl>*/}

                {/*<InputLabel style={{textAlign:"center", width:"100%", top:"10px"}}>Availability</InputLabel>*/}

                {/*<Stack direction="row" spacing={0} sx={{marginLeft: "10px"}} alignItems="center">*/}
                {/*    <Typography>Map</Typography>*/}
                {/*    <Switch  />*/}
                {/*</Stack>*/}

            </Box>
        </Box>
    );
}

export default SearchBar;